import { use, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { SessionContext } from "@features/auth/SessionContext";
import { createApiClient } from "@vendor/api-client";
import { WorkspaceContext } from "./WorkspaceContext";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string;

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const session = use(SessionContext);
  if (!session) throw new Error("WorkspaceProvider must render inside SessionProvider");

  const client = createApiClient({
    baseUrl: apiBaseUrl,
    getToken: () => session.session?.access_token ?? null,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["me"],
    queryFn: () => client.getMe(),
    staleTime: 5 * 60_000,
    enabled: !!session.session,
  });

  if (!session.session) return <>{children}</>;
  if (isLoading) return null;
  if (error || !data) throw error ?? new Error("Failed to load workspace");

  return (
    <WorkspaceContext value={{ user: data.user, workspace: data.workspace }}>
      {children}
    </WorkspaceContext>
  );
}
